/* eslint-disable @typescript-eslint/ban-ts-comment */
'use client';

import { Button } from '@repo/ui/components/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@repo/ui/components/sheet';
import { Menu } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { Link } from '../../i18n/navigation';
import { AuthButton } from '../auth/auth-button';
import { HeaderNavItem, PatchLink } from './header';

const defaultNavItems: HeaderNavItem[] = [
  { label: 'pricing', href: '/#pricing' },
  { label: 'faqs', href: '/#faq' },
  { label: 'blog', href: '/blog' },
];

interface MobileMenuProps {
  navItems: HeaderNavItem[];
  callToAction?: {
    label: 'get_started';
    href: string;
  };
}

export function MobileMenu({ navItems }: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations('common');
  const completeNavItems = [...defaultNavItems, ...navItems];

  return (
    <div className='flex items-center gap-4 md:hidden'>
      {/* Default navigation items shown when menu is closed */}
      {defaultNavItems.map((item) => (
        <Link
          key={item.label}
          href={item.href ?? ''}
          className='text-sm font-medium text-muted-foreground hover:text-foreground transition-colors'
        >
          {/* @ts-ignore */}
          {t(item.label)}
        </Link>
      ))}

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant='ghost' size='icon' className='hover:bg-transparent'>
            <Menu className='h-6 w-6 text-muted-foreground' />
            <span className='sr-only'>Toggle menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side='right' className='sm:w-80 p-0 flex flex-col'>
          <SheetHeader className='p-6 border-b'>
            <SheetTitle>
              <Link
                href='/'
                className='w-8 h-8 mt-2 flex items-center gap-2'
                onClick={() => setIsOpen(false)}
              >
                <Image
                  src='/imagik-logo-small.webp'
                  alt='Imagik'
                  width={32}
                  height={32}
                />
                <span className='font-medium text-xl'>Imagik</span>
              </Link>
            </SheetTitle>
          </SheetHeader>

          <div className='flex-1 overflow-y-auto'>
            <nav className='flex flex-col p-6'>
              {completeNavItems.map((item) => (
                <div key={item.label} className='flex flex-col gap-2 py-2'>
                  {item.items ? (
                    <>
                      <div className='text-base text-muted-foreground font-normal'>
                        {/* @ts-ignore */}
                        {t(item.label)}
                      </div>
                      <div className='flex flex-col gap-3'>
                        {item.items.map((subItem) => (
                          <PatchLink
                            key={subItem.title}
                            href={subItem.href}
                            onClick={() => setIsOpen(false)}
                            className='text-md font-medium text-foreground hover:text-foreground/80 transition-colors'
                          >
                            {/* @ts-ignore */}
                            {t(subItem.title)}
                          </PatchLink>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Link
                      href={item.href ?? ''}
                      onClick={() => setIsOpen(false)}
                      className='text-lg font-medium text-foreground hover:text-foreground/80 transition-colors'
                    >
                      {/* @ts-ignore */}
                      {t(item.label)}
                    </Link>
                  )}
                </div>
              ))}
            </nav>
          </div>

          <div className='mt-auto border-t p-6 flex justify-center'>
            <AuthButton className='w-full' onClick={() => setIsOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
